import { useState, useEffect, useRef, useCallback, useMemo } from "react";
import PopupModal from "../../components/ui/PopupModal";
import ConfirmationModal from "../../components/ui/ConfirmationModal";
import QRScanner from "../../components/ui/QRScanner";
import { useCoupons } from "../../hooks/useCoupons";
import { useCheckQRKey, useUseQRKey } from "../../hooks/useQRKey";
import QueryStateWrapper from "@/components/ui/QueryStateWrapper";
import { IoAlertCircle } from "react-icons/io5";
import QRInvoicePrintContent, {
  QRInvoiceData,
} from "./components/QRInvoicePrintContent";
import { toast } from "react-toastify";
import Button from "../../components/ui/Button";
import { useNavigate } from "react-router-dom";
import { useCurrentSession } from "@/hooks/useSession";

const QRSubscription = () => {
  const navigate = useNavigate();

  // فحص حالة الجلسة
  const {
    data: sessionData,
    isLoading: isLoadingSession,
    isError: isSessionError,
  } = useCurrentSession();

  // فحص إذا كانت الجلسة منتهية أو غير موجودة → توجيه إلى صفحة بدء الجلسة بدون تسجيل خروج تلقائي
  useEffect(() => {
    const sessionStarted = localStorage.getItem("sessionStarted");

    // إذا كان هناك خطأ في تحميل الجلسة
    if (isSessionError) {
      toast.error("خطأ في تحميل بيانات الجلسة، حاول مرة أخرى");
      return;
    }

    // إذا انتهى التحميل ولم توجد جلسة نشطة
    if (!isLoadingSession && !sessionData && sessionStarted) {
      toast.error("لا توجد جلسة نشطة، ابدأ جلسة جديدة");
      navigate("/start-session");
      return;
    }

    // إذا كانت الجلسة موجودة لكنها منتهية
    if (sessionData && sessionData.ended_at && sessionStarted) {
      toast.error("انتهت الجلسة، ابدأ جلسة جديدة");
      navigate("/start-session");
      return;
    }
  }, [sessionData, isLoadingSession, isSessionError, navigate]);

  const [manualCode, setManualCode] = useState("");
  const [scanned, setScanned] = useState(false);

  const [currentScannedCode, setCurrentScannedCode] = useState("");
  const [isMobile, setIsMobile] = useState(false);
  const [printInvoice, setPrintInvoice] = useState<QRInvoiceData | null>(null);
  const [lastOperation, setLastOperation] = useState<QRInvoiceData | null>(
    null
  );
  const [showReprintConfirm, setShowReprintConfirm] = useState(false);
  const printRef = useRef<HTMLDivElement>(null);
  const generateHTML = (): string | null => {
    if (!printRef.current) return null;
    return printRef.current.innerHTML;
  };

  // Helper: Print HTML in browser (fallback when WebView is not available)
  const printHtmlInBrowser = (html: string) => {
    try {
      const printWindow = window.open("", "_blank", "width=400,height=600");
      if (!printWindow) {
        return window.print();
      }
      printWindow.document.open();
      printWindow.document.write(`<!DOCTYPE html>
        <html dir="rtl" lang="ar">
          <head>
            <meta charset="UTF-8" />
            <title>Print</title>
            <style>
              @page { size: 58mm auto; margin: 0; }
              html, body { margin: 0; padding: 0; background: #fff; }
            </style>
          </head>
          <body>${html}</body>
        </html>`);
      printWindow.document.close();
      printWindow.focus();
      // Ensure content is rendered before printing
      printWindow.onload = () => {
        printWindow.print();
        setTimeout(() => {
          printWindow.close();
        }, 100);
      };
    } catch {
      window.print();
    }
  };

  // حالة للـ error popup
  const [errorPopup, setErrorPopup] = useState<null | {
    message: string;
  }>(null);

  // حالة للـ success confirmation
  const [successConfirm, setSuccessConfirm] = useState<null | {
    title: string;
    message: string;
    details: any;
  }>(null);

  const {
    data: couponsRaw = [],
    isLoading: isCouponsLoading,
    isError: isCouponsError,
    error: couponsError,
  } = useCoupons();
  const coupons: any[] = Array.isArray(couponsRaw) ? couponsRaw : [];

  // فحص فقط
  const checkQR = useCheckQRKey();
  // استخدام فعلي عند التأكيد
  const useQR = useUseQRKey();

  // دالة لتنسيق التاريخ فقط
  const formatDate = (dateString: string | undefined) => {
    if (!dateString) return "غير محدد";

    try {
      const date = new Date(dateString);
      return date.toLocaleDateString("ar-SA", {
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
      });
    } catch (error) {
      return dateString;
    }
  };

  useEffect(() => {
    const checkMobile = () => {
      const userAgent = navigator.userAgent.toLowerCase();
      const mobileRegex =
        /android|webos|iphone|ipad|ipod|blackberry|iemobile|opera mini/i;
      setIsMobile(mobileRegex.test(userAgent) || window.innerWidth <= 768);
    };

    checkMobile();
    window.addEventListener("resize", checkMobile);
    return () => window.removeEventListener("resize", checkMobile);
  }, []);

  useEffect(() => {
    const forceCleanup = () => {
      // عند الانتقال للخلفية أو إخفاء الصفحة لا نعطّل الإدخال
      setScanned(false);
      setCurrentScannedCode("");
      setErrorPopup(null);
      setSuccessConfirm(null);
    };

    const handleVisibilityChange = () => {
      if (document.hidden) {
        forceCleanup();
      }
    };

    const handleBeforeUnload = () => {
      forceCleanup();
    };

    const handlePageHide = () => {
      forceCleanup();
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);
    window.addEventListener("beforeunload", handleBeforeUnload);
    window.addEventListener("pagehide", handlePageHide);

    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
      window.removeEventListener("beforeunload", handleBeforeUnload);
      window.removeEventListener("pagehide", handlePageHide);
    };
  }, []);

  // تحميل آخر عملية من localStorage عند بدء التشغيل
  useEffect(() => {
    const savedOperation = localStorage.getItem("lastQROperation");
    if (savedOperation) {
      try {
        setLastOperation(JSON.parse(savedOperation));
      } catch (error) {
        console.error("Error parsing last operation:", error);
        localStorage.removeItem("lastQROperation");
      }
    }
  }, []);

  // حفظ العملية في localStorage
  const saveOperationToLocal = (data: QRInvoiceData) => {
    localStorage.setItem("lastQROperation", JSON.stringify(data));
    setLastOperation(data);
  };

  // إزالة العملية من localStorage
  const clearOperationFromLocal = () => {
    localStorage.removeItem("lastQROperation");
    setLastOperation(null);
  };

  const checkCouponCode = (code: string) => {
    if (!coupons || coupons.length === 0) {
      return null;
    }

    const cleanCode = code.trim();

    let match = coupons.find(
      (c) =>
        c.code.trim().toLowerCase() === cleanCode.toLowerCase() && c.is_active
    );

    if (match) {
      return match;
    }

    match = coupons.find((c) => {
      if (!c.is_active) return false;

      const couponCode = c.code.trim().toLowerCase();
      const inputCode = cleanCode.toLowerCase();

      return couponCode.includes(inputCode) || inputCode.includes(couponCode);
    });

    if (match) {
      return match;
    }

    const codeVariations = [
      cleanCode.replace(/[-_\s]/g, ""),
      cleanCode.replace(/[^a-zA-Z0-9]/g, ""),
      cleanCode.toUpperCase(),
      cleanCode.toLowerCase(),
    ];

    for (const variation of codeVariations) {
      if (variation.length > 0) {
        const variationMatch = coupons.find(
          (c) =>
            c.is_active &&
            c.code.trim().toLowerCase() === variation.toLowerCase()
        );
        if (variationMatch) {
          return variationMatch;
        }
      }
    }

    return null;
  };

  const handleCode = (code: string) => {
    if (!code || code.trim() === "") {
      setErrorPopup({
        message: "كود فارغ أو غير صالح",
      });
      return;
    }

    const cleanCode = code.trim();
    setCurrentScannedCode(cleanCode);

    // استخدام mutation بدلاً من query
    checkQR.mutate(cleanCode, {
      onSuccess: (result) => {
        if (result.status === "success" && result.data) {
          const responseData = result.data;

          // التحقق من وجود بيانات الاشتراك
          if (responseData.data?.subscription_info) {
            const subscriptionData = responseData.data;
            const subscriptionInfo = subscriptionData.subscription_info;
            const userInfo = subscriptionData.user_info;

            // حساب الغسلات المتبقية
            const totalUses = subscriptionData.num_of_uses;
            const usedByQR = subscriptionData.num_of_used_by_qr;
            const remainingWashes = totalUses - usedByQR;

            setSuccessConfirm({
              title: "تأكيد استخدام الاشتراك",
              message: `هل تريد خصم غسلة من اشتراك ${
                userInfo?.name || "العميل"
              }؟`,
              details: {
                type: "subscription",
                serviceName: subscriptionInfo.subscription.name,
                size: subscriptionInfo.size,
                userName: userInfo?.name,
                phone: userInfo?.phone,
                usage: `${subscriptionData.num_of_used_by_qr}/${subscriptionData.num_of_uses}`,
                remainingWashes: remainingWashes,
                endDate: subscriptionData.end_date,
                data: responseData,
              },
            });
          }
          // التحقق من وجود بيانات الخدمة (gift/service)
          else if (responseData.data?.service_info) {
            const serviceData = responseData.data;

            setSuccessConfirm({
              title: "تأكيد استخدام الخدمة",
              message: `هل تريد استخدام الخدمة المجانية؟`,
              details: {
                type: "service",
                serviceName: serviceData.service_info.name,
                size: serviceData.service_size_info?.size || "غير محدد",
                pointsSpent: serviceData.points_spent || 0,
                endAt: serviceData.end_at_qr,
                data: responseData,
              },
            });
          }
          // fallback للحالات الأخرى
          else {
            setSuccessConfirm({
              title: "تأكيد استخدام الكود",
              message: "هل تريد استخدام هذا الكود؟",
              details: {
                type: "unknown",
                data: responseData,
              },
            });
          }
        } else if (result.status === "error") {
          // عرض رسالة الخطأ من الباك اند
          const errorMessage = result.message || "حدث خطأ في قراءة الكود";
          setErrorPopup({
            message: errorMessage,
          });
        }
      },
      onError: (error: any) => {
        // التحقق من أخطاء الاتصال أولاً
        if (handleNetworkError(error)) {
          return;
        }

        // في حالة فشل QR Key، نجرب البحث في الكوبونات
        const couponMatch = checkCouponCode(cleanCode);
        if (couponMatch) {
          setSuccessConfirm({
            title: "تأكيد استخدام كوبون الخصم",
            message: `هل تريد استخدام كوبون الخصم؟`,
            details: {
              type: "coupon",
              code: couponMatch.code,
              discount: couponMatch.discount_percentage,
              data: couponMatch,
            },
          });
        } else {
          // عرض رسالة الخطأ من الباك اند إذا كانت متوفرة
          let errorMessage = "حدث خطأ في قراءة الكود";

          if (error?.response?.data?.detail) {
            errorMessage = error.response.data.detail;
          } else if (error?.response?.data?.message) {
            errorMessage = error.response.data.message;
          } else if (
            error?.response?.data?.non_field_errors &&
            Array.isArray(error.response.data.non_field_errors)
          ) {
            errorMessage = error.response.data.non_field_errors[0];
          } else if (error?.message) {
            errorMessage = error.message;
          }

          setErrorPopup({
            message: errorMessage,
          });
        }
      },
    });
  };

  const handleScanResult = useCallback(
    (decodedText: string) => {
      if (!scanned && decodedText) {
        let cleanedCode = decodedText
          .trim()
          .replace(/\s+/g, " ")
          .replace(/[^\x20-\x7E\u00A0-\u024F\u0600-\u06FF\u0660-\u0669]/g, "")
          .trim();

        if (/^[a-zA-Z0-9\-_]+$/.test(cleanedCode.replace(/\s/g, ""))) {
          cleanedCode = cleanedCode.replace(/\s/g, "");
        }

        setScanned(true);
        handleCode(cleanedCode);
      }
    },
    [scanned, handleCode]
  );

  const handleScanError = useCallback(
    (error: any) => {
      // تجاهل أخطاء معينة لا تحتاج لعرضها للمستخدم
      if (
        error?.message &&
        (error.message.includes("NotFoundException") ||
          error.message.includes("QRCodeScannerError") ||
          error.message.includes("Camera stream"))
      ) {
        return;
      }

      // تحديد نوع الخطأ وعرض رسالة مناسبة
      let errorMessage = "حدث خطأ في الكاميرا";

      if (error?.name === "NotReadableError") {
        errorMessage = isMobile
          ? "الكاميرا الخلفية مستخدمة في تطبيق آخر\nأغلق التطبيقات الأخرى وحاول مرة أخرى"
          : "الكاميرا مستخدمة من تطبيق آخر، أغلق التطبيقات الأخرى وحاول مرة أخرى";
      } else if (error?.name === "NotAllowedError") {
        errorMessage = isMobile
          ? "يرجى السماح للتطبيق بالوصول للكاميرا الخلفية\nمن إعدادات المتصفح"
          : "يرجى السماح للتطبيق بالوصول للكاميرا من إعدادات المتصفح";
      } else if (error?.name === "OverconstrainedError" && isMobile) {
        errorMessage =
          "لا توجد كاميرا خلفية متاحة\nجرب تحديث الصفحة أو استخدم الإدخال اليدوي";
      } else if (error?.name === "NotFoundError") {
        errorMessage = "لم يتم العثور على كاميرا متاحة";
      } else if (error?.name === "NotSupportedError") {
        errorMessage = "المتصفح لا يدعم الوصول للكاميرا";
      } else if (error?.name === "AbortError") {
        errorMessage = "تم إلغاء الوصول للكاميرا";
      } else if (error?.name === "SecurityError") {
        errorMessage = "خطأ أمني في الوصول للكاميرا";
      } else if (error?.message) {
        // إذا كان هناك رسالة خطأ من الباك اند أو النظام
        errorMessage = error.message;
      }

      setErrorPopup({
        message: errorMessage,
      });
    },
    [isMobile]
  );

  const scannerEl = useMemo(
    () => (
      <QRScanner
        paused={scanned}
        onResult={handleScanResult}
        onError={handleScanError}
      />
    ),
    [scanned, handleScanResult, handleScanError]
  );

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!manualCode.trim()) {
      setErrorPopup({
        message: "يرجى إدخال الكود أولاً",
      });
      return;
    }

    handleCode(manualCode);
  };

  const handleConfirmSuccess = () => {
    if (!successConfirm) return;

    // عند التأكيد: نستخدم الكود فعلياً ثم نبني بيانات الفاتورة من استجابة الاستخدام
    useQR.mutate(currentScannedCode, {
      onSuccess: (used) => {
        if (used.status === "success") {
          const responseData = used.data;
          const d = responseData?.data ?? responseData;

          let mapped: QRInvoiceData = {
            type: successConfirm.details.type,
            qrKey: currentScannedCode,
            date: new Date().toISOString(),
            time: new Date().toISOString(),
          } as QRInvoiceData;

          if (d?.subscription_info) {
            const totalUses = d.num_of_uses ?? 0;
            const usedByQR = d.num_of_used_by_qr ?? 0;
            mapped = {
              ...mapped,
              type: "subscription",
              userName: d.user_info?.name,
              phone: d.user_info?.phone,
              serviceName: d.subscription_info?.subscription?.name,
              size: d.subscription_info?.size,
              remainingWashes: Math.max(totalUses - usedByQR, 0),
              endDate: d.end_date,
            };
          } else if (d?.service_info) {
            mapped = {
              ...mapped,
              type: "service",
              serviceName: d.service_info?.name,
              size: d.service_size_info?.size,
              pointsSpent: d.points_spent ?? 0,
              endAt: d.end_at_qr,
            };
          } else if (successConfirm.details.type === "coupon") {
            mapped = {
              ...mapped,
              type: "coupon",
              code: d?.code ?? successConfirm.details.code,
              discount: d?.discount ?? successConfirm.details.discount,
            };
          }

          // حفظ العملية وبدء الطباعة
          saveOperationToLocal(mapped);
          setPrintInvoice(mapped);
          setSuccessConfirm(null);
          // تفريغ الحقول بعد الاستخدام الناجح للكود
          setScanned(false);
          setCurrentScannedCode("");
          setManualCode("");
          setTimeout(() => {
            if (printRef.current) {
              handlePrint();
            }
          }, 100);
        } else {
          // عرض رسالة الخطأ من الباك اند
          const errorMessage = used.message || "حدث خطأ في استخدام الكود";
          toast.error(errorMessage);
        }
      },
      onError: (error: any) => {
        // التحقق من أخطاء الاتصال أولاً
        if (handleNetworkError(error)) {
          return;
        }

        // عرض رسالة الخطأ من الباك اند إذا كانت متوفرة
        let errorMessage = "حدث خطأ في استخدام الكود";

        if (error?.response?.data?.detail) {
          errorMessage = error.response.data.detail;
        } else if (error?.response?.data?.message) {
          errorMessage = error.response.data.message;
        } else if (
          error?.response?.data?.non_field_errors &&
          Array.isArray(error.response.data.non_field_errors)
        ) {
          errorMessage = error.response.data.non_field_errors[0];
        } else if (error?.message) {
          errorMessage = error.message;
        }

        toast.error(errorMessage);
      },
    });
  };

  // معالج إعادة الطباعة
  const handleReprintOperation = () => {
    if (!lastOperation) return;
    setShowReprintConfirm(true);
  };

  // تأكيد إعادة الطباعة
  const handleConfirmReprint = () => {
    if (!lastOperation) return;

    setPrintInvoice(lastOperation);
    setShowReprintConfirm(false);

    // طباعة فورية
    setTimeout(() => {
      if (printRef.current) {
        handleReprintAfterPrint();
      }
    }, 100);
  };

  // معالج طباعة إعادة الطباعة (يحذف البيانات بعد الطباعة)
  const handleReprintAfterPrint = () => {
    try {
      const html = generateHTML();
      if (!html) {
        toast.error("خطأ في إنشاء محتوى الطباعة");
        return;
      }

      if ((window as any).ReactNativeWebView) {
        (window as any).ReactNativeWebView.postMessage(
          JSON.stringify({ action: "printInvoice", html })
        );
      } else {
        printHtmlInBrowser(html); // precise print for browser
      }

      setPrintInvoice(null);
      clearOperationFromLocal();
      toast.success("تم إعادة طباعة الإيصال بنجاح");
    } catch (error) {
      console.error("Reprint error:", error);
      toast.error("خطأ في إعادة الطباعة، حاول مرة أخرى");
    }
  };

  // إلغاء إعادة الطباعة
  const handleCancelReprint = () => {
    setShowReprintConfirm(false);
  };

  const handlePrint = () => {
    try {
      const html = generateHTML();
      if (!html) {
        toast.error("خطأ في إنشاء محتوى الطباعة");
        return;
      }

      if ((window as any).ReactNativeWebView) {
        (window as any).ReactNativeWebView.postMessage(
          JSON.stringify({ action: "printInvoice", html })
        );
      } else {
        printHtmlInBrowser(html);
      }
    } catch (error) {
      console.error("Print error:", error);
      toast.error("خطأ في عملية الطباعة، حاول مرة أخرى");
    }
  };

  const handleCancelSuccess = () => {
    setSuccessConfirm(null);
    setScanned(false);
    setCurrentScannedCode("");
    setManualCode("");
  };

  const isLoading = isCouponsLoading || checkQR.isPending || useQR.isPending;

  // إضافة معالجة خاصة لأخطاء الاتصال
  const handleNetworkError = (error: any) => {
    if (
      error?.code === "NETWORK_ERROR" ||
      error?.message?.includes("Network Error")
    ) {
      setErrorPopup({
        message:
          "خطأ في الاتصال بالخادم\nتأكد من اتصالك بالإنترنت وحاول مرة أخرى",
      });
      return true;
    }
    return false;
  };

  if (isCouponsError) {
    // تحديد رسالة الخطأ المناسبة
    let errorMessage =
      "تعذر تحميل قائمة الكوبونات. تأكد من اتصالك بالإنترنت وحاول مرة أخرى.";

    if (couponsError) {
      const error = couponsError as any; // Type assertion للوصول لخصائص response
      if (error?.response?.data?.detail) {
        errorMessage = error.response.data.detail;
      } else if (error?.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (
        error?.response?.data?.non_field_errors &&
        Array.isArray(error.response.data.non_field_errors)
      ) {
        errorMessage = error.response.data.non_field_errors[0];
      } else if (error?.message) {
        errorMessage = error.message;
      }
    }

    return (
      <div className='min-h-screen flex flex-col items-center justify-center bg-white p-4'>
        <IoAlertCircle className='text-6xl text-red-500 mb-4' />
        <h2 className='text-2xl font-bold text-gray-900 mb-4 text-center'>
          خطأ في تحميل البيانات
        </h2>
        <p className='text-gray-600 text-center mb-6 max-w-md'>
          {errorMessage}
        </p>
        <div className='space-y-3'>
          <button
            onClick={() => window.location.reload()}
            className='bg-red-700 text-white px-6 py-3 rounded-lg font-bold hover:bg-red-800 transition-colors'
          >
            إعادة المحاولة
          </button>
          <button
            onClick={() => navigate("/")}
            className='bg-gray-600 text-white px-6 py-3 rounded-lg font-bold hover:bg-gray-700 transition-colors block w-full'
          >
            العودة للصفحة الرئيسية
          </button>
        </div>
      </div>
    );
  }

  return (
    <QueryStateWrapper isLoading={isLoading} isError={false}>
      {/* Hidden Print Content */}
      {printInvoice && (
        <div
          style={{
            visibility: "hidden",
            position: "fixed",
            top: 0,
            left: 0,
            zIndex: -1,
          }}
        >
          <div ref={printRef}>
            <QRInvoicePrintContent data={printInvoice} />
          </div>
        </div>
      )}

      <div className='min-h-screen bg-white flex flex-col'>
        {/* Error Popup */}
        <PopupModal
          show={errorPopup !== null}
          type='error'
          message={errorPopup?.message || ""}
          icon='/icons/error.svg'
          onClose={() => {
            setErrorPopup(null);
            setScanned(false);
            setCurrentScannedCode("");
            setManualCode("");
          }}
        />

        {/* Success Confirmation Modal */}
        <ConfirmationModal
          show={successConfirm !== null}
          title={successConfirm?.title || ""}
          message={
            successConfirm
              ? successConfirm.message +
                (successConfirm.details.type === "subscription"
                  ? `\n\n• اسم العميل: ${
                      successConfirm.details.userName
                    }\n• رقم الهاتف: ${
                      successConfirm.details.phone
                    }\n• الخدمة: ${
                      successConfirm.details.serviceName
                    }\n• الحجم: ${
                      successConfirm.details.size
                    }\n• الغسلات المتبقية: ${
                      successConfirm.details.remainingWashes
                    } غسلة\n• تاريخ الانتهاء: ${formatDate(
                      successConfirm.details.endDate
                    )}`
                  : successConfirm.details.type === "service"
                  ? `\n\n• الخدمة: ${
                      successConfirm.details.serviceName
                    }\n• الحجم: ${
                      successConfirm.details.size
                    }\n• النقاط المستخدمة: ${
                      successConfirm.details.pointsSpent
                    }\n• ينتهي في: ${formatDate(successConfirm.details.endAt)}`
                  : successConfirm.details.type === "coupon"
                  ? `\n\n• كود الخصم: ${successConfirm.details.code}\n• نسبة الخصم: ${successConfirm.details.discount}%`
                  : "")
              : ""
          }
          confirmText='تأكيد وطباعة'
          cancelText='إلغاء'
          onConfirm={handleConfirmSuccess}
          onCancel={handleCancelSuccess}
        />

        {/* Reprint Confirmation Modal */}
        <ConfirmationModal
          show={showReprintConfirm}
          title='تأكيد إعادة الطباعة'
          message={
            lastOperation
              ? `هل تريد إعادة طباعة آخر عملية؟\n\nكود QR: ${lastOperation.qrKey}` +
                (lastOperation.type === "subscription"
                  ? `\n\n• اسم العميل: ${
                      lastOperation.userName
                    }\n• رقم الهاتف: ${lastOperation.phone}\n• الخدمة: ${
                      lastOperation.serviceName
                    }\n• الحجم: ${lastOperation.size}\n• الغسلات المتبقية: ${
                      lastOperation.remainingWashes
                    } غسلة\n• تاريخ الانتهاء: ${formatDate(
                      lastOperation.endDate || ""
                    )}`
                  : lastOperation.type === "service"
                  ? `\n\n• الخدمة: ${lastOperation.serviceName}\n• الحجم: ${
                      lastOperation.size
                    }\n• النقاط المستخدمة: ${
                      lastOperation.pointsSpent
                    }\n• ينتهي في: ${formatDate(lastOperation.endAt || "")}`
                  : lastOperation.type === "coupon"
                  ? `\n\n• كود الخصم: ${lastOperation.code}\n• نسبة الخصم: ${lastOperation.discount}%`
                  : "") +
                "\n\nملاحظة: ستُحذف البيانات بعد إعادة الطباعة"
              : ""
          }
          confirmText='تأكيد وطباعة'
          cancelText='إلغاء'
          onConfirm={handleConfirmReprint}
          onCancel={handleCancelReprint}
        />

        {/* Header */}
        <div className='text-center py-8'>
          <h1 className='text-4xl font-bold text-gray-800'>QRcode</h1>
        </div>

        {/* Main Content */}
        <div className='flex-1 flex flex-col items-center justify-center px-6 space-y-8'>
          {/* QR Scanner Area */}
          <div className='relative'>
            <div
              className={`relative overflow-hidden bg-gray-50 flex items-center justify-center border-2 border-gray-200 rounded-xl ${
                isMobile ? "w-72 h-72" : "w-80 h-80"
              }`}
            >
              {scannerEl}
              {isMobile && (
                <div className='absolute top-2 right-2 bg-black bg-opacity-50 text-white text-xs px-2 py-1 rounded'>
                  كاميرا خلفية
                </div>
              )}
            </div>
          </div>

          {/* Instructions Text */}
          <div className='text-center max-w-md'>
            <p className='text-gray-600 text-lg leading-relaxed'>
              {isMobile ? (
                <>
                  ضع رمز QR أمام الكاميرا الخلفية
                  <br />
                  للمسح التلقائي
                </>
              ) : (
                <>
                  ضع الرمز الشريطي ضمن حدود إطار
                  <br />
                  الكاميرا لمسحه
                </>
              )}
            </p>
            {isMobile && (
              <div className='mt-2 space-y-1'>
                <div className='flex items-center justify-center gap-2'>
                  <div className='w-2 h-2 bg-green-500 rounded-full animate-pulse'></div>
                  <span className='text-xs text-green-600'>
                    الكاميرا الخلفية نشطة
                  </span>
                </div>
                <p className='text-xs text-gray-500 text-center'>
                  في حالة عدم إغلاق الكاميرا، أعد تشغيل المتصفح
                </p>
              </div>
            )}
          </div>

          {/* Manual Input */}
          <form
            onSubmit={handleSubmit}
            className={`w-full space-y-6 ${isMobile ? "max-w-xs" : "max-w-md"}`}
          >
            <div
              className='relative'
              onFocus={() => setScanned(true)}
              onBlur={() => setScanned(false)}
            >
              <input
                type='text'
                placeholder={
                  isMobile ? "ادخل الكود يدوياً" : "ادخل QR code يدويا"
                }
                value={manualCode}
                onChange={(e) => setManualCode(e.target.value)}
                className={`w-full border-2 border-gray-300 rounded-lg px-4 py-4 text-right focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-red-500 transition-colors placeholder-gray-400 ${
                  isMobile ? "text-base" : "text-lg"
                }`}
                disabled={
                  checkQR.isPending || useQR.isPending || !!successConfirm
                }
                autoComplete='off'
              />
              {manualCode && (
                <button
                  type='button'
                  onClick={() => setManualCode("")}
                  className='absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600'
                >
                  ✕
                </button>
              )}
            </div>
            <div className='flex justify-between gap-4 mb-8'>
              {/* زر إعادة الطباعة */}
              {lastOperation && (
                <Button onClick={handleReprintOperation} type='secondary'>
                  إعادة طباعة آخر عملية
                </Button>
              )}

              <button
                type='submit'
                disabled={
                  !manualCode.trim() || checkQR.isPending || useQR.isPending
                }
                className={`w-full py-4 rounded-lg font-bold transition-all ${
                  isMobile ? "text-lg" : "text-xl"
                } ${
                  !manualCode.trim() || checkQR.isPending || useQR.isPending
                    ? "bg-gray-400 text-gray-600 cursor-not-allowed"
                    : "bg-red-600 text-white hover:bg-red-700 shadow-lg"
                }`}
              >
                {isMobile ? "تأكيد" : "تأكيد الطلب"}
              </button>
            </div>
          </form>
        </div>
      </div>
    </QueryStateWrapper>
  );
};

export default QRSubscription;
